import { useState, useEffect, useRef, useCallback, useId } from "react";
import { getHighSchoolList } from "../../../services/PublicServices";

// Module-level cache — fetched once per app session, shared across all renders.
// Safe: apiInstance is stable (useAxiosInstance uses useMemo([])) and
// /Public/high-school-list is unauthenticated, so cached data is always valid
// for the session lifetime regardless of which instance performed the fetch.
let _cache = null;
let _pendingFetch = null;

const loadSchools = async (apiInstance) => {
  if (_cache !== null) return _cache;
  if (_pendingFetch) return _pendingFetch;

  _pendingFetch = getHighSchoolList(apiInstance)
    .then((data) => {
      const list = Array.isArray(data)
        ? [
            ...new Set(
              data
                .map((item) =>
                  typeof item === "string"
                    ? item.trim()
                    : (item?.name ?? item?.schoolName ?? "").trim(),
                )
                .filter(Boolean),
            ),
          ]
        : [];
      _cache = list;
      _pendingFetch = null;
      return _cache;
    })
    .catch(() => {
      _pendingFetch = null;
      return [];
    });

  return _pendingFetch;
};

const normalize = (s) =>
  (s ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const HighlightMatch = ({ text, query }) => {
  if (!query || query.length < 2) return <>{text}</>;
  const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const parts = text.split(new RegExp(`(${escaped})`, "gi"));
  return (
    <>
      {parts.map((part, i) =>
        part.toLowerCase() === query.toLowerCase() ? (
          <mark
            key={i}
            className="bg-yellow-200 text-inherit rounded-sm font-semibold not-italic"
          >
            {part}
          </mark>
        ) : (
          <span key={i}>{part}</span>
        ),
      )}
    </>
  );
};

const HighSchoolAutocomplete = ({
  value,
  onChange,
  onBlur,
  apiInstance,
  hasError,
  inputId = "highSchool",
}) => {
  const [inputValue, setInputValue] = useState(value || "");
  const [suggestions, setSuggestions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [status, setStatus] = useState("idle"); // "idle" | "loading" | "done"
  const debounceRef = useRef(null);
  const blurTimerRef = useRef(null);
  const containerRef = useRef(null);
  const listRef = useRef(null);
  const uid = useId();
  const listId = `${uid}-list`;

  // Pre-warm the cache on mount so the first keystroke feels instant
  useEffect(() => {
    loadSchools(apiInstance);
  }, [apiInstance]);

  // Sync when form resets or external value changes
  useEffect(() => {
    setInputValue(value || "");
  }, [value]);

  const filter = useCallback(
    async (query) => {
      const schools = await loadSchools(apiInstance);
      const q = normalize(query);
      const matches = schools
        .filter((s) => normalize(s).includes(q))
        .slice(0, 8);
      setSuggestions(matches);
      setStatus("done");
      setActiveIndex(-1);
      setIsOpen(true);
    },
    [apiInstance],
  );

  const handleChange = (e) => {
    const val = e.target.value;
    setInputValue(val);
    onChange(val); // always keep form value in sync (free text allowed)

    if (val.length < 2) {
      clearTimeout(debounceRef.current);
      setSuggestions([]);
      setIsOpen(false);
      setStatus("idle");
      return;
    }

    setStatus("loading");
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => filter(val), 220);
  };

  const commit = (school) => {
    const trimmed = school.trim();
    setInputValue(trimmed);
    onChange(trimmed);
    setSuggestions([]);
    setIsOpen(false);
    setActiveIndex(-1);
  };

  const handleKeyDown = (e) => {
    if (!isOpen || suggestions.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => (i < suggestions.length - 1 ? i + 1 : i));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => (i > 0 ? i - 1 : 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (activeIndex >= 0) {
        commit(suggestions[activeIndex]);
      } else {
        setIsOpen(false);
      }
    } else if (e.key === "Escape") {
      setIsOpen(false);
    }
  };

  // Scroll active option into view
  useEffect(() => {
    if (activeIndex >= 0 && listRef.current) {
      listRef.current.children[activeIndex]?.scrollIntoView({
        block: "nearest",
      });
    }
  }, [activeIndex]);

  // Close on outside click; clean up debounce on unmount
  useEffect(() => {
    const close = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", close);
    return () => {
      document.removeEventListener("mousedown", close);
      clearTimeout(debounceRef.current);
      clearTimeout(blurTimerRef.current);
    };
  }, []);

  const showDropdown = isOpen && inputValue.length >= 2;
  const showLoading = showDropdown && status === "loading";
  const showList = showDropdown && status === "done" && suggestions.length > 0;
  const showNoResults =
    showDropdown && status === "done" && suggestions.length === 0;

  return (
    <div ref={containerRef} className="relative">
      <input
        id={inputId}
        type="text"
        value={inputValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={(e) => {
          blurTimerRef.current = setTimeout(() => setIsOpen(false), 150);
          onBlur(e);
        }}
        onFocus={() => {
          if (inputValue.length >= 2 && suggestions.length > 0) setIsOpen(true);
        }}
        placeholder="Enter high school name"
        autoComplete="off"
        spellCheck="false"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-controls={listId}
        aria-activedescendant={
          activeIndex >= 0 ? `${uid}-opt-${activeIndex}` : undefined
        }
        className={`w-full${hasError ? " border border-red-500" : ""}`}
      />

      {showDropdown && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
          {showLoading && (
            <p className="px-4 py-3 text-sm text-gray-500">Searching…</p>
          )}

          {showList && (
            <ul
              id={listId}
              ref={listRef}
              role="listbox"
              className="overflow-y-auto"
              style={{ maxHeight: 256 }}
            >
              {suggestions.map((school, i) => (
                <li
                  key={school}
                  id={`${uid}-opt-${i}`}
                  role="option"
                  aria-selected={i === activeIndex}
                  onMouseDown={(e) => {
                    e.preventDefault(); // keep focus on input
                    commit(school);
                  }}
                  onMouseEnter={() => setActiveIndex(i)}
                  className={`px-4 py-2.5 text-sm cursor-pointer transition-colors ${
                    i === activeIndex
                      ? "bg-red-50 text-red-800"
                      : "text-gray-800 hover:bg-gray-50"
                  }`}
                >
                  <HighlightMatch text={school} query={inputValue} />
                </li>
              ))}
            </ul>
          )}

          {showNoResults && (
            <p className="px-4 py-3 text-sm text-gray-500 italic">
              No matches found — continue typing to use a custom school name.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default HighSchoolAutocomplete;
